import React, { useState } from "react";
import { formatMomentDate } from "../../../../../utils/dateHelper";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EditIcon, Delete02Icon, PlusIcon } from "../../../../../assets/icons";
import CompanyAwardModal from "../../../../../components/modals/company/CompanyAwardModal";
import DeletePopup from "../../../../../components/delete-popup";
import {
  createCompanyAward,
  updateCompanyAward,
  deleteCompanyAward,
} from "../../../../../redux/slice/companySlice";
import PreviewModal from "../../../../../components/modals/previewModal";
import { usePermission } from "../../../../../hooks/usePermission";
import { useToast } from "../../../../../context/TostContext";
import { UserInital } from "../../../../../utils/otherHelopers";

const MODULE = "company_management";

const Awards = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMediaArray, setPreviewMediaArray] = useState([]);
  const [expandedAwards, setExpandedAwards] = useState({});

  const { companyDetail, awardActionLoading } = useSelector((state) => state.company);

  const toggleExpand = (id) => {
    setExpandedAwards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleUpsertAward = (data) => {
    const action = currentItem
      ? updateCompanyAward({ companyId: id, id: currentItem.id, data })
      : createCompanyAward({ companyId: id, data });

    dispatch(action)
      .unwrap()
      .then(() => {
        toast.success(`Award ${currentItem ? "updated" : "added"} successfully`);
        setShowModal(false);
        setCurrentItem(null);
      })
      .catch((error) => {
        toast.error(error?.message || `Failed to ${currentItem ? "update" : "add"} award`);
      });
  };

  const handleDeleteAward = () => {
    dispatch(deleteCompanyAward({ companyId: id, id: itemToDelete }))
      .unwrap()
      .then(() => {
        toast.success("Award deleted successfully");
        setShowDeletePopup(false);
        setItemToDelete(null);
      })
      .catch((error) => {
        toast.error(error?.message || "Failed to delete award");
      });
  };

  return (
    <div className="position-relative h-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 className="fw-bold mb-0" style={{ fontSize: "16px", color: "#444050" }}>
          Awards
        </h5>
        {hasPermission(MODULE, "update") && (
          <button
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
            onClick={() => {
              setCurrentItem(null);
              setShowModal(true);
            }}
          >
            <PlusIcon size={14} />
            Add Award
          </button>
        )}
      </div>

      {companyDetail?.awards?.length > 0 ? (
        <div className="row g-3">
          {companyDetail.awards.map((award, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="p-3 border rounded-3 bg-light-subtle position-relative custom-card h-100">
                <div className="position-absolute top-0 end-0 p-2 d-flex gap-2">
                  {hasPermission(MODULE, "update") && (
                    <button
                      className="post-action-btn edit-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setCurrentItem(award);
                        setShowModal(true);
                      }}
                    >
                      <EditIcon width={12} height={12} color="#007bff" />
                    </button>
                  )}
                  {hasPermission(MODULE, "delete") && (
                    <button
                      className="post-action-btn delete-btn border rounded p-1 bg-white"
                      onClick={() => {
                        setItemToDelete(award.id);
                        setShowDeletePopup(true);
                      }}
                    >
                      <Delete02Icon width={12} height={12} color="#FF0000" />
                    </button>
                  )}
                </div>
                <div className="d-flex gap-3 align-items-start">
                  <UserInital width={50} name={award.title} image={award.file?.url} onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewMediaArray([
                      {
                        url: award.file.url,
                        title: award.title || "Award Image",
                        type: "image",
                      },
                    ]);
                    setShowPreviewModal(true);
                  }}
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }} />
                  <div className="flex-grow-1 overflow-hidden" style={{ paddingRight: "75px" }}>
                    <h6 className="mb-1 fw-semibold text-dark text-break" style={{ fontSize: "14px" }}>
                      {award.title || "-"}
                    </h6>
                    <div className="d-flex align-items-center flex-wrap gap-2 text-muted mb-2" style={{ fontSize: "12px" }}>
                      <span className="fw-medium">Awarded by: {award.awarded_by || "-"}</span>
                      {award.award_date && (
                        <>
                          <span style={{ fontSize: "14px", lineHeight: 1 }}>•</span>
                          <span>{formatMomentDate(award.award_date, "MMM YYYY")}</span>
                        </>
                      )}
                    </div>
                    {award.description && (
                      <p className="mb-0 text-secondary text-break mt-1 small" style={{ lineHeight: "1.5" }}>
                        {award.description.length > 150 && !expandedAwards[award.id] ? (
                          <>
                            {award.description.slice(0, 150)}...{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(award.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show more
                            </span>
                          </>
                        ) : award.description.length > 150 ? (
                          <>
                            {award.description}{" "}
                            <span
                              className="text-primary fw-semibold ms-1"
                              onClick={() => toggleExpand(award.id)}
                              style={{ cursor: "pointer" }}
                            >
                              Show less
                            </span>
                          </>
                        ) : (
                          award.description
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted text-center py-4 small">No awards listed.</p>
      )}

      <CompanyAwardModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setCurrentItem(null);
        }}
        onSubmit={handleUpsertAward}
        award={currentItem}
        isLoading={awardActionLoading}
        title={currentItem ? "Edit Company Award" : "Add Company Award"}
        size="lg"
      />

      <DeletePopup
        show={showDeletePopup}
        onHide={() => {
          setShowDeletePopup(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteAward}
        content="Are you sure you want to delete this award? This action cannot be undone."
        saveTitle="Delete"
        disabled={awardActionLoading}
      />

      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={0}
      />
    </div>
  );
};

export default Awards;
